import { Skeleton as MuiSkeleton, type SkeletonProps as MuiSkeletonProps } from '@mui/material';
import { borderRadius, color as token } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { forwardRef } from 'react';
import { FF_FPL_MIGRATION } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export type SkeletonProps = MuiSkeletonProps;

/** Pulse: opacity dip, matching MUI's `pulseKeyframe` (1 → 0.4 → 1). */
const pulse = stylex.keyframes({
  '0%': { opacity: 1 },
  '50%': { opacity: 0.4 },
  '100%': { opacity: 1 },
});

/** Wave: a highlight band swept left→right across the placeholder, matching MUI's `waveKeyframe`. */
const wave = stylex.keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '60%': { transform: 'translateX(100%)' },
  '100%': { transform: 'translateX(100%)' },
});

const styles = stylex.create({
  base: {
    display: 'block',
    height: '1.2em',
    // Neutral grey skeleton surface (dark ≈ #383838), matching the legacy translucent-white overlay
    // tone. Brand `bgComponent*` tokens are purple-tinted, so they are deliberately not used here.
    backgroundColor: token.bgSecondary,
    // One uniform corner radius for every skeleton (circular overrides below); per-call-site
    // `sx.borderRadius` is legacy-only and intentionally not reflected on the flag-on branch.
    borderRadius: borderRadius.small,
  },
  // MUI text variant: auto height from a non-breaking-space line box, scaled to 60% visible height.
  text: {
    height: 'auto',
    marginTop: 0,
    marginBottom: 0,
    transformOrigin: '0 55%',
    transform: 'scale(1, 0.6)',
    '::before': {
      content: '"\\00a0"',
      display: 'block',
    },
  },
  circular: { borderRadius: '50%' },
  pulseAnim: {
    animationName: pulse,
    animationDuration: '2s',
    animationTimingFunction: 'ease-in-out',
    animationDelay: '0.5s',
    animationIterationCount: 'infinite',
  },
  // Wave needs a clipped, positioned box for the swept `::after` highlight band.
  waveAnim: {
    position: 'relative',
    overflow: 'hidden',
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      transform: 'translateX(-100%)',
      backgroundImage: `linear-gradient(90deg, transparent, ${token.bordertranslucent}, transparent)`,
      animationName: wave,
      animationDuration: '2s',
      animationTimingFunction: 'linear',
      animationDelay: '0.5s',
      animationIterationCount: 'infinite',
    },
  },
});

const dynamic = stylex.create({
  size: (width?: number | string, height?: number | string) => ({ width, height }),
});

/**
 * Weave skeleton placeholder and the `fpl-migration` boundary.
 *
 * - Flag off: the legacy MUI `Skeleton`, with every existing prop (`variant`, `width`, `height`,
 *   `animation`, `sx`, `className`, `data-testid`, …) unchanged.
 * - Flag on: a native `<span>` styled with StyleX and FPL tokens. FPL ships no Skeleton, so the
 *   visuals are owned locally and tuned for continuity — MUI's pulse/wave motion and the text-variant
 *   60% scale are reproduced; the neutral background and a single uniform corner radius come from FPL
 *   tokens (circular renders a full circle). `sx` is legacy-only and is not read on this branch.
 */
const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(function Skeleton(props, ref) {
  const { value: isFplMigration } = useFeatureFlag(FF_FPL_MIGRATION);

  if (isFplMigration) {
    // `sx` is legacy-only; pulled out of `rest` so it never lands on the native element.
    const {
      variant = 'text',
      animation = 'pulse',
      width,
      height,
      className,
      style,
      sx: _sx,
      children: _children,
      ...rest
    } = props;

    const styled = stylex.props(
      styles.base,
      variant === 'text' ? styles.text : variant === 'circular' ? styles.circular : null,
      animation === 'pulse' && styles.pulseAnim,
      animation === 'wave' && styles.waveAnim,
      (width != null || height != null) && dynamic.size(width ?? undefined, height ?? undefined),
    );

    return (
      <span
        ref={ref}
        {...rest}
        className={[styled.className, className].filter(Boolean).join(' ')}
        style={{ ...styled.style, ...style }}
      />
    );
  }

  return <MuiSkeleton ref={ref} {...props} />;
});

export { Skeleton };
